import { useEffect, useRef } from 'react'
import { Colors } from 'design/colors'

export interface AngleErrorSample {
  timestampMs: number
  valueDeg: number
  isOffsetPhase?: boolean
}

interface AngleErrorPlotProps {
  samples: AngleErrorSample[]
  width?: number
  height?: number
  windowMs?: number
}

const PLOT_BG_COLOR = Colors.DARK_GREEN
const PLOT_LINE_COLOR = '#4fc3f7'
const PLOT_GRID_COLOR = 'rgba(255,255,255,0.1)'
const PLOT_ZERO_COLOR = 'rgba(255,255,255,0.3)'
const PLOT_TEXT_COLOR = Colors.WHITE
const PLOT_OFFSET_ZONE_COLOR = 'rgba(255,200,50,0.15)'
const PLOT_OFFSET_LABEL_COLOR = 'rgba(255,200,50,0.6)'

const PAD = { top: 14, right: 14, bottom: 28, left: 55 }
const Y_TICKS = 5

type Pixel = (n: number) => number

const setupCanvas = (canvas: HTMLCanvasElement, width: number, height: number) => {
  const canvasContext = canvas.getContext('2d')
  if (!canvasContext) return null
  const dpr = window.devicePixelRatio || 1
  canvas.width = width * dpr
  canvas.height = height * dpr
  canvasContext.scale(dpr, dpr)
  canvasContext.fillStyle = PLOT_BG_COLOR
  canvasContext.fillRect(0, 0, width, height)
  return canvasContext
}

const drawEmptyState = (canvasContext: CanvasRenderingContext2D, width: number, height: number) => {
  canvasContext.fillStyle = PLOT_TEXT_COLOR
  canvasContext.font = '14px monospace'
  canvasContext.textAlign = 'center'
  canvasContext.fillText('Waiting for data...', width / 2, height / 2)
}

const drawGrid = (canvasContext: CanvasRenderingContext2D, plotW: number, plotH: number) => {
  canvasContext.strokeStyle = PLOT_GRID_COLOR
  canvasContext.lineWidth = 0.5
  for (let i = 0; i <= Y_TICKS; i++) {
    const y = PAD.top + (i / Y_TICKS) * plotH
    canvasContext.beginPath()
    canvasContext.moveTo(PAD.left, y)
    canvasContext.lineTo(PAD.left + plotW, y)
    canvasContext.stroke()
  }
}

const drawZeroLine = (canvasContext: CanvasRenderingContext2D, plotW: number, zeroY: number) => {
  canvasContext.strokeStyle = PLOT_ZERO_COLOR
  canvasContext.lineWidth = 1
  canvasContext.beginPath()
  canvasContext.moveTo(PAD.left, zeroY)
  canvasContext.lineTo(PAD.left + plotW, zeroY)
  canvasContext.stroke()
}

const drawOffsetZones = (
  canvasContext: CanvasRenderingContext2D,
  visible: AngleErrorSample[],
  xToPixel: Pixel,
  plotH: number
) => {
  canvasContext.fillStyle = PLOT_OFFSET_ZONE_COLOR
  let inZone = false
  let zoneStartX = 0
  for (let i = 0; i < visible.length; i++) {
    const s = visible[i]
    const x = xToPixel(s.timestampMs)
    if (s.isOffsetPhase && !inZone) {
      inZone = true
      zoneStartX = x
    } else if (!s.isOffsetPhase && inZone) {
      inZone = false
      canvasContext.fillRect(zoneStartX, PAD.top, x - zoneStartX, plotH)
    }
  }
  if (inZone) {
    const endX = xToPixel(visible[visible.length - 1].timestampMs)
    canvasContext.fillRect(zoneStartX, PAD.top, endX - zoneStartX, plotH)
  }
}

const drawOffsetLabel = (
  canvasContext: CanvasRenderingContext2D,
  visible: AngleErrorSample[],
  xToPixel: Pixel
) => {
  const offsetSamples = visible.filter((s) => s.isOffsetPhase)
  if (offsetSamples.length === 0) return
  const midX = xToPixel(offsetSamples[Math.floor(offsetSamples.length / 2)].timestampMs)
  canvasContext.fillStyle = PLOT_OFFSET_LABEL_COLOR
  canvasContext.font = '12px monospace'
  canvasContext.textAlign = 'center'
  canvasContext.textBaseline = 'top'
  canvasContext.fillText('offset', midX, PAD.top + 4)
}

const drawLine = (
  canvasContext: CanvasRenderingContext2D,
  visible: AngleErrorSample[],
  xToPixel: Pixel,
  yToPixel: Pixel
) => {
  canvasContext.strokeStyle = PLOT_LINE_COLOR
  canvasContext.lineWidth = 1.5
  canvasContext.beginPath()
  visible.forEach((s, i) => {
    const x = xToPixel(s.timestampMs)
    const y = yToPixel(s.valueDeg)
    if (i === 0) canvasContext.moveTo(x, y)
    else canvasContext.lineTo(x, y)
  })
  canvasContext.stroke()
}

const drawYAxisLabels = (
  canvasContext: CanvasRenderingContext2D,
  plotH: number,
  yMax: number,
  yMin: number
) => {
  canvasContext.fillStyle = PLOT_TEXT_COLOR
  canvasContext.font = '13px monospace'
  canvasContext.textAlign = 'right'
  canvasContext.textBaseline = 'middle'
  for (let i = 0; i <= Y_TICKS; i++) {
    const val = yMax - (i / Y_TICKS) * (yMax - yMin)
    const y = PAD.top + (i / Y_TICKS) * plotH
    canvasContext.fillText(val.toFixed(1) + '°', PAD.left - 6, y)
  }
}

const drawXAxisLabel = (
  canvasContext: CanvasRenderingContext2D,
  width: number,
  height: number,
  windowMs: number
) => {
  canvasContext.fillStyle = PLOT_TEXT_COLOR
  canvasContext.font = '13px monospace'
  canvasContext.textAlign = 'center'
  canvasContext.textBaseline = 'top'
  canvasContext.fillText('Offset (°) — last ' + windowMs / 1000 + 's', width / 2, height - 20)
}

export const AngleErrorPlot = ({
  samples,
  width = 350,
  height = 140,
  windowMs = 30000
}: AngleErrorPlotProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const canvasContext = setupCanvas(canvas, width, height)
    if (!canvasContext) return

    if (samples.length < 2) {
      drawEmptyState(canvasContext, width, height)
      return
    }

    const plotW = width - PAD.left - PAD.right
    const plotH = height - PAD.top - PAD.bottom

    const now = samples[samples.length - 1].timestampMs
    const tMin = now - windowMs
    const visible = samples.filter((s) => s.timestampMs >= tMin)
    if (visible.length < 2) return

    const yMax = Math.ceil(Math.max(...visible.map((s) => Math.abs(s.valueDeg)), 1))
    const yMin = -yMax

    const xToPixel: Pixel = (t) => PAD.left + ((t - tMin) / windowMs) * plotW
    const yToPixel: Pixel = (v) => PAD.top + ((yMax - v) / (yMax - yMin)) * plotH

    drawGrid(canvasContext, plotW, plotH)
    drawZeroLine(canvasContext, plotW, yToPixel(0))
    drawOffsetZones(canvasContext, visible, xToPixel, plotH)
    drawOffsetLabel(canvasContext, visible, xToPixel)
    drawLine(canvasContext, visible, xToPixel, yToPixel)
    drawYAxisLabels(canvasContext, plotH, yMax, yMin)
    drawXAxisLabel(canvasContext, width, height, windowMs)
  }, [samples, width, height, windowMs])

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height, borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)' }}
    />
  )
}
